import { Box, Checkbox, Grid, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import TimePicker from '../../TimePicker/TimePicker';
import { DaysOfWeek, DaysOfWeekSchedule } from '../../../types/booking';
import { days, mapFromAvailability, subtractMinutes } from '../../../utils/booking';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchCoachAvailabilities } from '../../../redux/booking/bookingSlice';
import BookingAvailabilitySkeleton from './BookingAvailabilitySkeleton';
import { hideScrollbarStyles } from '../../../utils/styles';

export const StyledCheckbox = styled(Checkbox)({
  color: '#6CA3AC',
  '&.Mui-checked': {
    color: '#6CA3AC',
  },
});

type ISetAvailabilityStepProps = {
  timePickerVisibility: DaysOfWeek | {};
  setTimePickerVisibility: (_timePickerVisibility: DaysOfWeek | {}) => void;
  times: DaysOfWeekSchedule | {};
  setTimes: (_times: DaysOfWeekSchedule | {}) => void;
  availabilitiesError: Record<string, boolean>;
  hasPermissions: boolean;
};

function SetAvailabilityStep({
  timePickerVisibility,
  setTimePickerVisibility,
  times,
  setTimes,
  availabilitiesError,
  hasPermissions,
}: ISetAvailabilityStepProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const langCode = language.split('-')[0];
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { coachAvailabilities, fetchCoachAvailabilitiesLoading } = useAppSelector(
    (state) => state.booking
  );
  const coach = useAppSelector((state) => state.coach.coach);
  const dispatch = useAppDispatch();
  // Function to toggle the visibility of time picker sections for a particular day
  const toggleTimePickerVisibility = (day: string) => {
    setTimePickerVisibility((prevState) => ({
      ...prevState,
      [day]: !prevState[day],
    }));
  };

  // Function to handle time change for a particular day and time type (start or end)
  const handleTimeChange = (day: string, type: string, newTime: string) => {
    setTimes((prevTimes) => ({
      ...prevTimes,
      [day]: {
        ...prevTimes[day],
        [type]: newTime,
      },
    }));
  };

  useEffect(() => {
    if (coach.id) {
      dispatch(fetchCoachAvailabilities({ coachId: coach.id }));
    }
  }, [coach.id]);

  useEffect(() => {
    if (coachAvailabilities && coachAvailabilities?.length > 0) {
      const { status, schedule } = mapFromAvailability(coachAvailabilities);
      setTimePickerVisibility(status);
      setTimes(schedule);
    }
  }, [coachAvailabilities]);

  const areAllDaysHidden = () => days.every((day) => !timePickerVisibility[day]);

  if (fetchCoachAvailabilitiesLoading) {
    return <BookingAvailabilitySkeleton />;
  }

  return (
    <Grid display="flex" flexDirection="column" alignItems="center" height="calc(100% - 68px)">
      <Grid gap={6} display="flex" flexDirection="column" alignItems="center">
        <Typography variant="title1Bold" color="primary">
          {t('bookings.setAvailabilityStep.title')}
        </Typography>
        <Typography variant="bodyRegular" color="primary" maxWidth={542} textAlign="center">
          {t('bookings.setAvailabilityStep.subTitle')}
        </Typography>
      </Grid>
      <Grid
        display="flex"
        justifyContent="flex-start"
        alignItems="flex-start"
        m="auto"
        gap={2}
        width="100%"
        flexDirection="column"
        maxWidth={570}
        maxHeight="450px"
        pr={2}
        sx={{ ...hideScrollbarStyles, overflowY: 'auto', overflowX: 'hidden' }}
      >
        {!areAllDaysHidden() && (
          <Grid display="flex" flexDirection="row" gap="20px" ml="178px">
            <Typography
              variant="subheadBold"
              color="primary"
              width={isSmallScreen ? 155 : 178}
              textAlign="center"
            >
              {t('bookings.setAvailabilityStep.startTime')}
            </Typography>
            <Typography
              variant="subheadBold"
              color="primary"
              width={isSmallScreen ? 155 : 178}
              textAlign="center"
            >
              {t('bookings.setAvailabilityStep.endTime')}
            </Typography>
          </Grid>
        )}
        {days.map((day) => (
          <Grid key={day} display="flex" flexDirection="row" gap="20px" alignItems="center">
            <Grid display="flex" flexDirection="row" gap="12px" alignItems="center">
              <StyledCheckbox
                checked={timePickerVisibility[day]} // Set checked state based on visibility
                onChange={() => toggleTimePickerVisibility(day)} // Toggle visibility on change
                inputProps={{ 'aria-label': 'controlled' }}
                checkedIcon={
                  <Box
                    component="img"
                    src="/assets/icons/check-box-checked.svg"
                    width={20}
                    height={20}
                  />
                }
                icon={
                  <Box component="img" src="/assets/icons/check-box.svg" width={20} height={20} />
                }
                disabled={!hasPermissions}
              />
              <Typography variant="bodyBold" color="primary" width={104}>
                {t(day)}
              </Typography>
            </Grid>
            {/* Render TimePicker only if visibility is true */}
            {timePickerVisibility[day] ? (
              <Grid display="flex" flexDirection="row" gap="20px">
                <TimePicker
                  time={times[day].start}
                  handleOneTimeChange={(_field, value: string) => {
                    handleTimeChange(day, 'start', value);
                  }}
                  locale={langCode}
                  maxTime={subtractMinutes(times[day].end, true)}
                  digitalTime
                  availabilitiesError={availabilitiesError[day]}
                  hasPermissions={hasPermissions}
                />

                <TimePicker
                  time={times[day].end}
                  handleOneTimeChange={(_field, value: string) => {
                    handleTimeChange(day, 'end', value);
                  }}
                  locale={langCode}
                  minTime={subtractMinutes(times[day].start, false)} // Pass selected start time as minTime
                  digitalTime
                  availabilitiesError={availabilitiesError[day]}
                  hasPermissions={hasPermissions}
                />
              </Grid>
            ) : (
              <Typography variant="bodyRegular" color="primary">
                {t('bookings.setAvailabilityStep.notAvailable')}
              </Typography>
            )}
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
}

export default SetAvailabilityStep;
